import { useCallback, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { PageHeader, REPORT_PAGE_SX } from "../../../../components/ui";
import dayjs from "dayjs";
import { ClioService, LloCasesToFormItem, OpenAPI } from "../../../../api";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { LloCasesToFormFilters } from "./LloCasesToFormFilters";
import { LloCasesToFormTable } from "./LloCasesToFormTable";
import { applyFilters, resetFilters, uniqueStringOptions } from "./filtering";
import { sortRows } from "./sorting";
import {
  DEFAULT_SORT_RULES,
  EMPTY_FILTERS,
  type LloCasesToFormFiltersState,
  type SortRule,
} from "./types";

const MySwal = withReactContent(Swal);

export default function LloCasesToForm() {
  const [reportData, setReportData] = useState<LloCasesToFormItem[]>([]);
  const [filters, setFilters] = useState<LloCasesToFormFiltersState>(EMPTY_FILTERS);
  const [sortRules, setSortRules] = useState<SortRule[]>([...DEFAULT_SORT_RULES]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadReport = useCallback(() => {
    setLoading(true);
    ClioService.getApiClioLloCasesToForm()
      .then((data) => {
        setReportData(data);
        setHasLoaded(true);
        setPage(0);
      })
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error loading LLO cases to form.</p>,
        })
      )
      .finally(() => setLoading(false));
  }, []);

  const filterOptions = useMemo(
    () => ({
      caseName: uniqueStringOptions(reportData, "caseName"),
      stage: uniqueStringOptions(reportData, "stage"),
      raa: uniqueStringOptions(reportData, "raa"),
      ap: uniqueStringOptions(reportData, "ap"),
    }),
    [reportData]
  );

  const filteredData = useMemo(
    () => applyFilters(reportData, filters),
    [reportData, filters]
  );

  const sortedData = useMemo(
    () => sortRows(filteredData, sortRules),
    [filteredData, sortRules]
  );

  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const applyFilterState = useCallback(
    (nextFilters: LloCasesToFormFiltersState, nextSortRules: SortRule[]) => {
      setFilters(nextFilters);
      setSortRules(nextSortRules.length > 0 ? nextSortRules : [...DEFAULT_SORT_RULES]);
      setPage(0);
    },
    []
  );

  const clearFilters = useCallback(() => {
    setFilters(resetFilters());
    setPage(0);
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${OpenAPI.BASE}/api/Clio/lloCasesToForm/download`, {
        method: "POST",
        headers: {
          Authorization: (OpenAPI.HEADERS as Record<string, string>)?.Authorization,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items: sortedData }),
      });

      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `LloCasesToForm-${dayjs().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      MySwal.fire({ icon: "error", title: <p>Error downloading LLO cases to form.</p> });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={REPORT_PAGE_SX}>
      <PageHeader
        title="LLO cases to form"
        subtitle="Limited litigation cases whose complaint was filed 4 or more months ago."
      />

      <LloCasesToFormFilters
        filters={filters}
        filterOptions={filterOptions}
        sortRules={sortRules}
        loading={loading}
        downloading={downloading}
        canDownload={hasLoaded && sortedData.length > 0}
        onLoad={loadReport}
        onApply={applyFilterState}
        onDownload={handleDownload}
        onClear={clearFilters}
      />

      <LloCasesToFormTable
        loading={loading}
        hasLoadedData={hasLoaded}
        rows={paginatedData}
        totalCount={sortedData.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(next) => {
          setRowsPerPage(next);
          setPage(0);
        }}
      />
    </Box>
  );
}
